import React from "react";
import { EyeOffIcon } from "@components/Common/SvgIcons/EyeOffIcon";
import { EyeIcon } from "@components/Common/SvgIcons/EyeIcon";
import { useTranslation } from "react-i18next";

import {
    Card,
    CardTitle,
    LanguageTabs,
    LanguageTab,
    ColumnHeaders,
    ColumnHeaderLabel,
    ResetButton,
    InfoRow,
    InfoRowLabel,
    InfoInput,
    InfoActions,
    EyeButton,
    MiniToggle,
    ResetIconButton,
} from "./styled";
import { LANGUAGES, RestaurantText } from "./types";
import { ResetIcon } from "@components/Common/SvgIcons/ResetIcon";
import { ICON_SIZE_SMALL } from "../shared/constants";

type RestaurantInfoCardProps = {
    mainColor: string;
    selectedLangRestaurant: string;
    onSelectLangRestaurant: (code: string) => void;
    currentTexts: RestaurantText;
    currentDefaults: RestaurantText;
    nameVisible: boolean;
    descriptionVisible: boolean;
    onRestaurantTextChange: (
        field: "name" | "description",
        value: string
    ) => void;
    onToggleNameVisible: () => void;
    onToggleDescriptionVisible: () => void;
    onResetRestaurantField: (field: "name" | "description") => void;
    onResetAllRestaurant: () => void;
};

export function RestaurantInfoCard({
    mainColor,
    selectedLangRestaurant,
    onSelectLangRestaurant,
    currentTexts,
    currentDefaults,
    nameVisible,
    descriptionVisible,
    onRestaurantTextChange,
    onToggleNameVisible,
    onToggleDescriptionVisible,
    onResetRestaurantField,
    onResetAllRestaurant,
}: RestaurantInfoCardProps): JSX.Element {
    const { t } = useTranslation();

    return (
        <Card data-testid="scan-to-misc-restaurant-info-card">
            <CardTitle>{t("Titre et Description")}</CardTitle>

            <LanguageTabs data-testid="scan-to-misc-restaurant-language-tabs">
                {LANGUAGES.map((lang) => (
                    <LanguageTab
                        key={lang.code}
                        data-testid={`scan-to-misc-restaurant-lang-tab-${lang.code}`}
                        isActive={selectedLangRestaurant === lang.code}
                        mainColor={mainColor}
                        onClick={() => onSelectLangRestaurant(lang.code)}
                    >
                        {lang.label}
                    </LanguageTab>
                ))}
            </LanguageTabs>

            <ColumnHeaders>
                <div />
                <ColumnHeaderLabel>{t("Texte par défaut")}</ColumnHeaderLabel>
                <ColumnHeaderLabel>{t("Personnalisé")}</ColumnHeaderLabel>
                <ResetButton
                    data-testid="scan-to-misc-reset-all-restaurant-btn"
                    mainColor={mainColor}
                    onClick={onResetAllRestaurant}
                    title={t("Réinitialiser")}
                    aria-label={t("Réinitialiser")}
                >
                    <ResetIcon style={{ width: ICON_SIZE_SMALL, height: ICON_SIZE_SMALL }} />
                </ResetButton>
            </ColumnHeaders>

            {/* Titre row */}
            <InfoRow data-testid="scan-to-misc-title-row">
                <InfoRowLabel>{t("Titre")}</InfoRowLabel>
                <InfoInput
                    data-testid="scan-to-misc-title-default-input"
                    value={currentDefaults.name}
                    disabled
                />
                <InfoInput
                    data-testid="scan-to-misc-title-custom-input"
                    value={currentTexts.name}
                    onChange={(e) =>
                        onRestaurantTextChange("name", e.target.value)
                    }
                />
                <InfoActions>
                    <EyeButton
                        data-testid="scan-to-misc-title-visibility-btn"
                        isVisible={nameVisible}
                        mainColor={mainColor}
                        onClick={onToggleNameVisible}
                        title={nameVisible ? t("Visible") : t("Hidden")}
                        aria-label={nameVisible ? t("Hide") : t("Show")}
                        aria-pressed={nameVisible}
                    >
                        {nameVisible ? <EyeIcon /> : <EyeOffIcon />}
                    </EyeButton>
                    <MiniToggle
                        data-testid="scan-to-misc-title-toggle"
                        isActive={nameVisible}
                        mainColor={mainColor}
                        onClick={onToggleNameVisible}
                    />
                    <ResetIconButton
                        data-testid="scan-to-misc-reset-title-btn"
                        mainColor={mainColor}
                        onClick={() => onResetRestaurantField("name")}
                        title={t("Reset")}
                        aria-label={t("Reset")}
                    >
                        <ResetIcon style={{ width: ICON_SIZE_SMALL, height: ICON_SIZE_SMALL }} />
                    </ResetIconButton>
                </InfoActions>
            </InfoRow>

            {/* Description row */}
            <InfoRow data-testid="scan-to-misc-description-row">
                <InfoRowLabel>{t("Description")}</InfoRowLabel>
                <InfoInput
                    data-testid="scan-to-misc-description-default-input"
                    value={currentDefaults.description}
                    disabled
                />
                <InfoInput
                    data-testid="scan-to-misc-description-custom-input"
                    value={currentTexts.description}
                    onChange={(e) =>
                        onRestaurantTextChange("description", e.target.value)
                    }
                />
                <InfoActions>
                    <EyeButton
                        data-testid="scan-to-misc-description-visibility-btn"
                        isVisible={descriptionVisible}
                        mainColor={mainColor}
                        onClick={onToggleDescriptionVisible}
                        title={descriptionVisible ? t("Visible") : t("Hidden")}
                        aria-label={descriptionVisible ? t("Hide") : t("Show")}
                        aria-pressed={descriptionVisible}
                    >
                        {descriptionVisible ? <EyeIcon /> : <EyeOffIcon />}
                    </EyeButton>
                    <MiniToggle
                        data-testid="scan-to-misc-description-toggle"
                        isActive={descriptionVisible}
                        mainColor={mainColor}
                        onClick={onToggleDescriptionVisible}
                    />
                    <ResetIconButton
                        data-testid="scan-to-misc-reset-description-btn"
                        mainColor={mainColor}
                        onClick={() => onResetRestaurantField("description")}
                        title={t("Reset")}
                        aria-label={t("Reset")}
                    >
                        <ResetIcon style={{ width: ICON_SIZE_SMALL, height: ICON_SIZE_SMALL }} />
                    </ResetIconButton>
                </InfoActions>
            </InfoRow>
        </Card>
    );
}
